import { ClientPlan } from '@maru44/huntre-utils/src/models/client'
import { ClientMemberRole } from '@maru44/huntre-utils/src/models/clientMember'
import type { ClientSubGroupInput } from '@maru44/huntre-utils/src/models/clientSubGroup'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { Avatar, Box, Button, Chip, IconButton, Paper, Stack, TextField, Typography } from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { IoCopySharp } from 'react-icons/io5'
import { useNavigate, useParams } from 'react-router-dom'
import { BaseDialog } from 'src/components/atoms/BaseDialog'
import { Breads } from 'src/components/atoms/BreadCrumbs'
import { ClientSubGroupFormDialog } from 'src/components/dialogs/clientSubGroup/ClientSubGroupFormDialog'
import { ProUpgradeNotice } from 'src/components/organisms/pro/ProUpgradeNotice'
import { ClientContext } from 'src/components/providers/ClientProvider'
import { baseURL, lineId } from 'src/config'
import { useClientDevices } from 'src/hooks/clientDevice/useClientDevices'
import { useClientSubGroups } from 'src/hooks/clientSubGroup/useClientSubGroups'
import { useStorage } from 'src/hooks/storage/useStorage'
import { capture } from 'src/utils/sentry'

export const ClientSubGroupPage = () => {
  const { client, currentMember, clientMembers, clientSubGroups } = useContext(ClientContext)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const clientId = client?.id ?? ''

  const { updateClientSubGroup, deleteClientSubGroup, clearClientSubGroupLineTarget } = useClientSubGroups(clientId)
  const { listClientDevicesWithDevice } = useClientDevices(clientId)
  const { getFileURL } = useStorage()

  const [lineQR, setLineQR] = useState<string | null>(null)
  const [openClearDialog, setOpenClearDialog] = useState(false)
  const [openEdit, setOpenEdit] = useState(false)

  const subGroup = useMemo(() => clientSubGroups.find((sg) => sg.id === id), [clientSubGroups, id])

  const memberNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const m of clientMembers) map.set(m.id, m.name)
    return map
  }, [clientMembers])

  const isPro = client?.plan === ClientPlan.Pro
  const isAdmin = currentMember?.role === ClientMemberRole.Admin
  const canManage = isAdmin && isPro

  useEffect(() => {
    getFileURL('static/line/M_gainfriends_2dbarcodes_GW.png', setLineQR)
  }, [getFileURL])

  const handleDelete = useCallback(async () => {
    if (!subGroup) return
    if (!confirm(`サブグループ「${subGroup.name}」を削除しますか?`)) return
    try {
      await deleteClientSubGroup(subGroup.id)
      toast.success('削除しました')
      navigate(`/clients/${clientId}/subGroups`)
    } catch (e) {
      capture(e)
      toast.error('削除に失敗しました')
    }
  }, [subGroup, deleteClientSubGroup, navigate, clientId])

  const handleClearLine = useCallback(async () => {
    if (!subGroup) return
    try {
      await clearClientSubGroupLineTarget(subGroup.id)
      toast.success('LINE連携を解除しました')
      setOpenClearDialog(false)
    } catch (e) {
      capture(e)
      toast.error('LINE連携の解除に失敗しました')
    }
  }, [subGroup, clearClientSubGroupLineTarget])

  const handleCopyCode = useCallback(async () => {
    if (!subGroup) return
    await navigator.clipboard.writeText(`code: ${clientId}/clientSubGroups/${subGroup.id}`)
    toast.success('コードをコピーしました。そのままLINEにご入力ください。')
  }, [clientId, subGroup])

  const handleEdit = useCallback(
    async (input: ClientSubGroupInput) => {
      if (!subGroup) return
      await updateClientSubGroup(subGroup.id, input)
    },
    [subGroup, updateClientSubGroup],
  )

  if (!isPro) {
    return (
      <Box>
        <Breads breadIds={['subGroups']} />
        <Stack spacing={2}>
          <Typography variant="h5">サブグループ</Typography>
          <ProUpgradeNotice clientId={clientId} isAdmin={isAdmin} />
        </Stack>
      </Box>
    )
  }

  if (!subGroup) {
    return (
      <Box>
        <Breads breadIds={['subGroups', 'subGroup']} />
        <Typography>サブグループが見つかりません</Typography>
      </Box>
    )
  }

  return (
    <>
      <Box>
        <Breads breadIds={['subGroups', 'subGroup']} />
        <Stack spacing={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography variant="h5">{subGroup.name}</Typography>
            {canManage && (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setOpenEdit(true)}>
                編集
              </Button>
            )}
          </Box>
          {!canManage && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'info.light',
                borderRadius: 1,
              }}
            >
              {isPro ? '編集にはグループの管理者権限が必要です。' : 'サブグループの編集は Pro プランの管理者のみ可能です。'}
            </Box>
          )}

          <Paper elevation={6} sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Section title="対象デバイス">
                {subGroup.clientDeviceIds.length === 0 ? (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    未設定
                  </Typography>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{
                      flexWrap: 'wrap',
                    }}
                  >
                    {subGroup.clientDeviceIds.map((id) => (
                      <Chip key={id} label={id} variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Section>

              <Section title="通知先メンバー">
                {subGroup.clientMemberIds.length === 0 ? (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    未設定
                  </Typography>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{
                      flexWrap: 'wrap',
                    }}
                  >
                    {subGroup.clientMemberIds.map((mid) => (
                      <Chip key={mid} label={memberNameById.get(mid) ?? mid} variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Section>

              <Section title="LINE通知先">
                {subGroup.lineTarget ? (
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Paper sx={{ width: 240, padding: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Avatar src={subGroup.lineTarget.pictureUrl ?? `${baseURL}/user.png`} sx={{ width: 30, height: 30 }} />
                        <Typography
                          sx={{
                            mt: 1,
                            width: '100%',
                            textAlign: 'center',
                            wordBreak: 'break-word',
                          }}
                        >
                          {subGroup.lineTarget.displayName ?? '(名前なし)'}
                          {subGroup.lineTarget.type === 'group' && ' (LINEグループ)'}
                          {subGroup.lineTarget.type === 'room' && ' (LINEルーム)'}
                        </Typography>
                      </Box>
                    </Paper>
                    {canManage && (
                      <Button variant="outlined" onClick={() => setOpenClearDialog(true)}>
                        連携を解除する
                      </Button>
                    )}
                  </Stack>
                ) : (
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    未設定
                  </Typography>
                )}
              </Section>
            </Stack>
          </Paper>

          {canManage && !subGroup.lineTarget && (
            <Paper elevation={6} sx={{ p: 3 }}>
              <Stack spacing={2}>
                <Typography variant="h6">LINE通知先の設定</Typography>
                <Typography>
                  以下のQRコードもしくは
                  <a href={`https://line.me/R/ti/p/@${lineId}`} target="_blank" rel="noopener">
                    こちらのリンク
                  </a>
                  から公式アカウントを友達追加・LINEグループ・LINEルームに招待してください。
                  <br />
                  その後、下記のコードを通知先のトークルームに送信するとこのサブグループの通知先として設定されます。
                </Typography>
                {lineQR && <img src={lineQR} width={240} alt="LINE QR" />}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <TextField sx={{ flexGrow: 1 }} disabled value={`code: ${clientId}/clientSubGroups/${subGroup.id}`} />
                  <IconButton onClick={handleCopyCode}>
                    <IoCopySharp />
                  </IconButton>
                </Box>
              </Stack>
            </Paper>
          )}

          {canManage && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
              }}
            >
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                このサブグループを削除
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
      <BaseDialog
        open={openClearDialog}
        title="LINE連携の解除"
        content="LINE連携を解除しますか？"
        actionText="解除する"
        onClose={() => setOpenClearDialog(false)}
        onAction={handleClearLine}
      />
      <ClientSubGroupFormDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSubmit={handleEdit}
        initialValue={subGroup}
        clientDevices={(listClientDevicesWithDevice.data ?? []).map((v) => v.clientDevice)}
        clientMembers={clientMembers}
      />
    </>
  )
}

type SectionProps = {
  title: string
  children: React.ReactNode
}

const Section = ({ title, children }: SectionProps) => {
  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          color: 'text.secondary',
          mb: 1,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  )
}
